import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { clearGlobalError } from "../store/reducers/errorSlice";
import {
  ApiFormErrorMapping,
  resolveApiFormError,
} from "../utils/errors-utils";

export interface UseApiFormErrorsOptions<F extends string>
  extends ApiFormErrorMapping<F> {
  /**
   * Replace the message the API sent for a field. Return undefined (or nothing)
   * to keep the API's own wording, which is what we show by default: the BFF
   * has already translated it, so it is displayed as given and never looked up
   * as a key. Only override where the backend is known to send a raw key.
   */
  fieldMessage?: (field: F, apiMessage: string) => string | undefined;
}

export interface ApiFormErrorState<F extends string> {
  /** Message per input, keyed by this form's own field names. */
  fieldErrors: Partial<Record<F, string>>;
  /**
   * Form-level message: the rejection's own message, and every field the API
   * named that this form has no input for.
   */
  formMessage: string | null;
  /**
   * False only when the rejection carried neither fields nor a message, which
   * is the one case where the generic error modal is still the right answer.
   */
  handled: boolean;
}

export interface ApiFormErrors<F extends string> extends ApiFormErrorState<F> {
  /** Feed a rejection in. Returns the same state it just stored. */
  handleError: (error: unknown) => ApiFormErrorState<F>;
  clear: () => void;
  /**
   * Drop one field's message, for an input to call as its value changes
   * (MED-1532): an error the server raised against the old value stops being
   * true the moment the user edits it, so it must not sit there until the next
   * submit. The form-level message is left alone — it is not about this input.
   */
  clearField: (field: F) => void;
}

const EMPTY = <F extends string>(): ApiFormErrorState<F> => ({
  fieldErrors: {},
  formMessage: null,
  handled: false,
});

/**
 * MED-1476 / MED-1477 / MED-1458: the API's 400s are precise — they name the
 * offending field (`{"code":"VALIDATION_FAILED","fields":[{"name":"promotionCode",…}]}`)
 * or explain the rejection (`PROMOTION_REBATE_INVALID`). Every form used to
 * throw that away and let the interceptor's generic "an unexpected error
 * occurred" modal stand in for it, sometimes stacked on top of the dialog the
 * user was still in.
 *
 * This is that handling, once, for any form to apply:
 *  - a rejection carrying `fields` puts each message on its own input, and the
 *    global modal is cleared rather than shown alongside;
 *  - a rejection carrying only `message` shows that message;
 *  - a rejection carrying neither is left to the generic modal (`handled: false`).
 *
 * A field name the form does not map is surfaced in `formMessage` — it is never
 * swallowed.
 */
export function useApiFormErrors<F extends string>(
  options: UseApiFormErrorsOptions<F> = {}
): ApiFormErrors<F> {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [state, setState] = useState<ApiFormErrorState<F>>(EMPTY<F>);

  // Read through a ref so `handleError` stays stable across renders even though
  // callers pass a fresh options object every time.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const handleError = useCallback(
    (error: unknown): ApiFormErrorState<F> => {
      const { fieldMessage, ...mapping } = optionsRef.current;
      const resolved = resolveApiFormError<F>(error, mapping);

      const fieldErrors: Partial<Record<F, string>> = {};
      for (const entry of resolved.fieldErrors) {
        fieldErrors[entry.field] =
          fieldMessage?.(entry.field, entry.message) ||
          entry.message ||
          t("apiErrors.fieldRejected");
      }

      const parts = resolved.unmappedFields.map(field =>
        field.name
          ? t("apiErrors.unmappedFieldError", {
              field: field.name,
              message: field.message || t("apiErrors.fieldRejected"),
            })
          : field.message
      );
      if (resolved.message) parts.push(resolved.message);

      const next: ApiFormErrorState<F> = {
        fieldErrors,
        formMessage: parts.length > 0 ? parts.join(" ") : null,
        handled: resolved.handled,
      };

      if (next.handled) {
        // The interceptor has already raised the generic modal. These messages
        // replace it instead of stacking under it (MED-1476).
        dispatch(clearGlobalError());
      }
      setState(next);
      return next;
    },
    [dispatch, t]
  );

  const clear = useCallback(() => setState(EMPTY<F>()), []);

  const clearField = useCallback((field: F) => {
    setState(prev => {
      if (prev.fieldErrors[field] === undefined) return prev;
      const fieldErrors = { ...prev.fieldErrors };
      delete fieldErrors[field];
      return { ...prev, fieldErrors };
    });
  }, []);

  return { ...state, handleError, clear, clearField };
}
